import { ReactElement, useState } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  useTranslate,
} from 'react-admin';
import { Box, Button, Card, Dialog, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import { XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE } from './constants';
import './init';

const XpxPerpsWithdrawalList = () => {
  const t = useTranslate();
  const [popup, setPopup] = useState<ReactElement>();

  return (
    <DashboardContent>
      <PermissionGuard resource={XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE} action="read" fallback={<ErrorPage403 />}>
        <CustomBreadcrumbs
          heading={t('module.xpxPerpsWithdrawal.title')}
          links={[{ name: t('module.xpxPerpsWithdrawal.title') }, { name: t('resource.list', { _: 'List' }) }]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
          >
            <Box sx={{ overflow: 'auto', position: 'relative' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
              >
                <TextField source="userId" label="module.xpxPerpsWithdrawal.userId" sortable={false} />
                <TextField source="toAddress" label="module.xpxPerpsWithdrawal.toAddress" sortable={false} />
                <TextField source="chainId" label="module.xpxPerpsWithdrawal.chainId" sortable={false} />
                <TextField source="token" label="module.xpxPerpsWithdrawal.token" sortable={false} />
                <TextField source="amount" label="module.xpxPerpsWithdrawal.amount" sortable={false} />
                <FunctionField
                  label="module.xpxPerpsWithdrawal.createdAt"
                  sortable={false}
                  render={(record: any) =>
                    record.createdAtUnixMs
                      ? dayjs(Number(record.createdAtUnixMs)).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                  }
                />
                <FunctionField
                  label=""
                  sortable={false}
                  render={(record: any) => (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <PermissionGuard resource={XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE} action="approve" fallback={<></>}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <ApproveDialog record={record} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        >
                          {t('module.xpxPerpsWithdrawal.approve')}
                        </Button>
                      </PermissionGuard>
                      <PermissionGuard resource={XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE} action="reject" fallback={<></>}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <RejectDialog record={record} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        >
                          {t('module.xpxPerpsWithdrawal.reject')}
                        </Button>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XpxPerpsWithdrawalList;
